import { useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  LogOut,
  Settings,
  User,
  Menu,
  Bell,
  ChevronDown,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useTenant } from "@/hooks/useTenant"
import { useAuth } from "@/hooks/useAuth"
import { useTheme } from "@/hooks/useTheme"
import { useNotifications } from "@/hooks/useNotifications"
import { detectStage } from "@/lib/stage"
import { UserAvatar } from "@/components/ui/UserAvatar"
import { StageBadge } from "@/components/layout/StageBadge"
import { useSeletorDoTopo } from "@/hooks/useSeletorDoTopo"
import { nomeNoSeletor } from "@/lib/leituraDaRede"

interface HeaderProps {
  onMenuToggle?: () => void
}


function UserMenu({
  name,
  email,
  avatarUrl,
  onSignOut,
  podeVerConfiguracoes,
}: {
  name: string
  email: string
  avatarUrl: string | null
  onSignOut: () => void
  /** Configurações é de tenant_admin pra cima — ver o comentário no `Header`. */
  podeVerConfiguracoes: boolean
}) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const menuRef = useRef<HTMLDivElement>(null)

  // Fecha ao clicar fora ou apertar Escape (mesmo padrão do GlassSelect).
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleEsc)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleEsc)
    }
  }, [open])

  return (
    <div ref={menuRef} className="relative shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-2 rounded-xl p-0.5 md:p-1.5 transition shrink-0",
          "hover:bg-glass-hover"
        )}
        aria-label="Menu do usuário"
      >
        <UserAvatar name={name} avatarUrl={avatarUrl} size="sm" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className={cn(
              "absolute top-full mt-2 right-0 w-60 max-w-[calc(100vw-16px)] z-50 rounded-2xl overflow-hidden",
              "backdrop-blur-[30px] bg-glass-modal border border-glass-border",
              "shadow-xl shadow-black/10 py-1"
            )}
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.15 }}
          >
            <div className="px-4 py-3 border-b border-glass-border flex items-center gap-3">
              <UserAvatar name={name} avatarUrl={avatarUrl} size="lg" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                  {name}
                </p>
                <p className="text-xs text-[var(--text-secondary)] truncate">{email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setOpen(false)
                navigate("/perfil")
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-glass-hover transition"
            >
              <User className="w-4 h-4" />
              Perfil
            </button>
            {podeVerConfiguracoes && (
              <button
                onClick={() => {
                  setOpen(false)
                  navigate("/configuracoes/funil")
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-glass-hover transition"
              >
                <Settings className="w-4 h-4" />
                Configurações
              </button>
            )}
            <div className="border-t border-glass-border mt-1" />
            <button
              onClick={() => {
                setOpen(false)
                onSignOut()
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-500/[0.08] transition"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { user, profile, signOut, role, canAccess } = useAuth()

  /**
   * Configurações é de administrador da ESC pra cima — a mesma régua que o menu
   * lateral usa (`minRole: "tenant_admin"`).
   *
   * 🚨 O CABEÇALHO NÃO PERGUNTAVA (17/08/2026). O menu lateral escondia certo,
   * mas aqui em cima o link aparecia pra todo mundo. Um vendedor clicava e
   * batia em "Acesso negado" — a tranca segurava, então não era falha de
   * segurança, mas oferecer uma porta trancada é convidar pro erro. Visto em
   * produção com o login de vendedor.
   */
  const podeVerConfiguracoes = canAccess("tenant_admin")
  const { tenants, setSelectedScope } = useTenant()

  /**
   * As opções, o valor e a cor do seletor vêm de `useSeletorDoTopo`: fora do
   * Financeiro, o escopo global de sempre; dentro dele, só as unidades com o
   * Financeiro, no escopo efetivo do módulo (a unidade sem o Financeiro não
   * aparece, e o seletor nunca fica em branco). Escolher uma opção muda o escopo
   * global, como em qualquer tela.
   */
  const seletor = useSeletorDoTopo()
  // const { isDark, toggleTheme } = useTheme() // dark mode disabled
  useTheme() // still call to force light mode cleanup
  const { unreadCount } = useNotifications()
  const navigate = useNavigate()
  // task #145: oculta o sino de notificações em produção (consistente com
  // o item "Notificações" da Sidebar). Em alfa/beta o sino continua visível.
  const hideNotificationBell = detectStage() === "producao"

  const handleSignOut = async () => {
    await signOut()
    navigate("/login")
  }

  // Derive display name and email from profile + supabase user
  const displayName = profile?.full_name ?? user?.email?.split("@")[0] ?? "Usuário"
  const displayEmail = user?.email ?? ""
  const avatarPublicUrl = profile?.avatar_url ?? null

  return (
    <header
      className={cn(
        "sticky top-0 z-40 flex h-14 items-center gap-1 md:gap-4 px-2 md:px-6",
        "backdrop-blur-[30px] bg-glass-header border-b border-glass-border"
      )}
    >
      {/* Left: hamburger + logo */}
      <div className="flex items-center gap-1 md:gap-3 shrink-0">
        <button
          onClick={onMenuToggle}
          className="md:hidden rounded-lg p-1 hover:bg-glass-hover transition"
          aria-label="Abrir menu"
        >
          <Menu className="w-5 h-5 text-[var(--text-primary)]" />
        </button>

        <Link
          to="/funil"
          className="flex items-center gap-1.5 md:gap-2 hover:opacity-80 transition shrink-0"
        >
          <img
            src="/logo.png"
            alt="CreditOS"
            className="h-4 md:h-7 w-auto object-contain shrink-0"
            style={{ maxWidth: "none" }}
          />
          <StageBadge />
        </Link>
      </div>

      {/* Center: Nav links (desktop) */}
      <nav className="hidden md:flex items-center gap-1 ml-4">
        <Link
          to="/funil"
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-glass-hover transition"
        >
          Funil
        </Link>
        {podeVerConfiguracoes && (
          <Link
            to="/configuracoes/funil"
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-glass-hover transition"
          >
            Configurações
          </Link>
        )}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right: tenant switcher + theme + user */}
      <div className="flex items-center gap-0.5 md:gap-2 shrink-0">
        {/* Global unit selector */}
        {tenants.length > 1 && (
          <div className="relative shrink-0">
            <select
              value={seletor.valor}
              onChange={(e) => setSelectedScope(e.target.value)}
              title={
                seletor.soComFinanceiro
                  ? "No Financeiro, a lista mostra as unidades em que ele está ligado. 'Todas as unidades' soma só essas."
                  : role === "seller"
                    ? "Filtre por unidade para ver leads daquela unidade. 'Todas as unidades' mostra todos os seus leads atribuídos em qualquer unidade que você atende."
                    : "Filtre por unidade para focar em leads daquela unidade. 'Todas as unidades' mostra todos os leads das unidades onde você é membro."
              }
              style={{ colorScheme: "dark" }}
              className={cn(
                "appearance-none pr-6 md:pr-7 py-1.5 rounded-lg text-xs font-medium cursor-pointer",
                seletor.cor ? "pl-5 md:pl-6" : "pl-2 md:pl-3",
                "max-w-[90px] sm:max-w-none",
                "backdrop-blur-[16px] bg-glass-card",
                "border border-glass-border",
                "text-[var(--text-primary)]",
                "focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent-blue-rgb)/0.40)]",
                "transition-colors"
              )}
              aria-label="Filtrar por unidade"
            >
              <option value="all">Todas as unidades</option>
              {seletor.unidades.map((t) => (
                <option key={t.id} value={t.id}>{nomeNoSeletor(t)}</option>
              ))}
            </select>
            <ChevronDown
              className="absolute right-1.5 md:right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[var(--text-tertiary)] pointer-events-none"
              aria-hidden="true"
            />
            {/* Color dot indicator */}
            {seletor.cor && (
              <span
                className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full pointer-events-none"
                style={{ backgroundColor: seletor.cor }}
              />
            )}
          </div>
        )}

        {/* Theme toggle — hidden while dark mode is disabled */}
        {/* <button
          onClick={toggleTheme}
          className="rounded-lg p-1 md:p-1.5 hover:bg-glass-hover transition shrink-0"
          aria-label={isDark ? "Modo claro" : "Modo escuro"}
        >
          {isDark ? (
            <Sun className="w-4 h-4 text-[var(--text-secondary)]" />
          ) : (
            <Moon className="w-4 h-4 text-[var(--text-secondary)]" />
          )}
        </button> */}

        {/* Notification bell — oculto em produção via task #145.
            Página /notificacoes continua acessível por URL direta. */}
        {!hideNotificationBell && (
          <button
            onClick={() => navigate("/notificacoes")}
            className="relative rounded-lg p-1 md:p-1.5 hover:bg-glass-hover transition shrink-0"
            aria-label="Notificações"
          >
            <Bell className="w-5 h-5 text-[var(--text-secondary)]" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-sm ring-2 ring-[var(--glass-header,#fff)]">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        )}

        {user && (
          <UserMenu
            name={displayName}
            email={displayEmail}
            avatarUrl={avatarPublicUrl}
            onSignOut={handleSignOut}
            podeVerConfiguracoes={podeVerConfiguracoes}
          />
        )}
      </div>
    </header>
  )
}
